import React from 'react';
import { Calculator, Wallet } from 'lucide-react';
import { Button } from '../../components/Button';
import { useToast } from '../../store/ToastContext';
import { getPaymentApp, buildPaymentUrl } from '../../services/paymentLinks';
import { getTabStatus } from '../../utils/tabCalculations';
import { formatCurrency } from '../../utils/currencyFormatting';
import type { Tab } from '../../types';
import { cn } from '../../utils/cn';

interface TabFooterProps {
    tab: Tab;
    myTotal: number;
    billTotal: number;
    isOwner: boolean;
    currentUserId?: string;
    onTogglePaid: (uid: string) => void;
    onToggleComplete?: () => void;
}

export const TabFooter: React.FC<TabFooterProps> = ({ tab, myTotal, billTotal, isOwner, currentUserId, onTogglePaid, onToggleComplete }) => {
    const { showToast } = useToast();

    const isPaid = currentUserId && (tab.completedMembers ?? []).includes(currentUserId);

    const handlePay = () => {
        if (!tab.paymentConfig) return;
        const app = getPaymentApp(tab.paymentConfig.appId);
        const url = buildPaymentUrl(
            tab.paymentConfig.appId,
            tab.paymentConfig.username,
            app?.supportsAmount ? myTotal : undefined,
            tab.currency,
            `${tab.name} – Evenly`,
        );
        window.open(url, '_blank');
    };

    const handleCopySummary = () => {
        const summary = `My share for ${tab.name}: ${formatCurrency(myTotal, tab.currency)}`;
        void navigator.clipboard.writeText(summary);
        showToast("Summary copied!", "success");
    };

    return (
        <div className="fixed bottom-[var(--navbar-height)] left-0 right-0 max-w-md mx-auto bg-slate-900 border-t border-slate-800 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.3)] z-30">
            <div className="max-w-md mx-auto text-white">
                <div className="flex items-end justify-between mb-3 border-b border-slate-800 pb-3">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-text-faint mb-0.5">Your Share</p>
                        <div className="flex items-baseline gap-2">
                            <p className="text-xl font-black">{formatCurrency(myTotal, tab.currency)}</p>
                            {(tab.tipPercentage > 0 || tab.serviceChargePercentage > 0) && (
                                <p className="text-[9px] text-text-muted">
                                    (+{tab.tipPercentage + tab.serviceChargePercentage}%)
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        {(() => {
                            const status = getTabStatus(tab);
                            const statusColors = {
                                complete: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
                                all_paid: "text-blue-400 bg-blue-400/10 border-blue-400/20",
                                in_progress: "text-amber-400 bg-amber-400/10 border-amber-400/20",
                                not_started: "text-slate-400 bg-slate-400/10 border-slate-400/20"
                            };
                            const statusText = {
                                complete: "Complete",
                                all_paid: "All Paid",
                                in_progress: "In Progress",
                                not_started: "Not Started"
                            };
                            return (
                                <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase tracking-wider ${statusColors[status]}`}>
                                    {statusText[status]}
                                </div>
                            );
                        })()}
                        <p className="text-xs font-bold text-text-faint">Total: {formatCurrency(billTotal, tab.currency)}</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    {/* Owner Actions */}
                    {isOwner ? (
                        <div className="flex-1 flex gap-2">
                            <Button
                                className={cn(
                                    "flex-1 border-none rounded-lg h-9 text-xs font-bold transition-all",
                                    tab.isComplete
                                        ? "bg-slate-700 text-slate-300 hover:bg-slate-600"
                                        : "bg-emerald-500 text-white hover:bg-emerald-600"
                                )}
                                onClick={onToggleComplete}
                            >
                                {tab.isComplete ? "Reopen Tab" : "Mark Complete"}
                            </Button>
                        </div>
                    ) : (
                        /* Member Actions */
                        tab.paymentConfig ? (
                            <Button
                                className="flex-1 bg-primary-500 text-white hover:bg-primary-600 border-none rounded-lg h-9 text-sm font-bold"
                                onClick={handlePay}
                            >
                                <Wallet className="mr-2 h-4 w-4" />
                                {(() => {
                                    const app = getPaymentApp(tab.paymentConfig.appId);
                                    return app ? `Pay via ${app.name}` : 'Pay Now';
                                })()}
                            </Button>
                        ) : (
                            <div className="flex-1 rounded-xl bg-bg-card/5 border border-white/10 flex items-center justify-center">
                                <p className="text-[9px] text-text-muted font-medium italic text-center leading-tight">No payment link<br />added by owner</p>
                            </div>
                        )
                    )}


                    {/* Paid Toggle - Only for non-owners (members) */}
                    {!isOwner && currentUserId && (
                        <Button
                            className={cn(
                                "h-9 px-3 rounded-lg text-xs font-bold transition-all border",
                                isPaid
                                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/50 hover:bg-emerald-500/30"
                                    : "bg-transparent text-slate-400 border-slate-700 hover:bg-white/5 hover:text-white"
                            )}
                            onClick={() => { onTogglePaid(currentUserId); }}
                        >
                            {isPaid ? "Paid ✓" : "Mark Paid"}
                        </Button>
                    )}

                    <Button
                        variant="outline"
                        className="border-white/10 text-white hover:bg-bg-card/5 rounded-lg h-9 w-9 p-0 shrink-0"
                        onClick={handleCopySummary}
                    >
                        <Calculator size={18} />
                    </Button>
                </div>
            </div>
        </div>
    );
};
